import React from 'react';
import type { TFunction } from 'i18next';
import { Lock } from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { GetSurveyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  ContractorTypeObj,
  SurveyFrequencyObj,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';

import { SurveyPreviewLink } from './SurveyPreviewLink';

interface SurveySearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
}

export const surveyForGrid = (
  t: TFunction<'administrationLng', 'administrationLng'>,
  tk: TKeys<'administrationLng'>,
  survey: GetSurveyDto,
  lang: string,
): GridRecord => {
  // Sprawdź czy ankieta jest zablokowana (startDate <= dzisiaj)
  const isLocked = (() => {
    if (!survey.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(survey.startDate);
    startDate.setHours(0, 0, 0, 0);
    return survey.active && startDate <= today;
  })();

  return {
    key: survey.id,
    values: {
      id: survey.id,
      'survey.systemNumber': (
        <>
          {survey.systemNumber}
          {isLocked && (
            <>
              {' '}
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.survey.surveyLocked)}</Tooltip>}
              >
                <Lock className="text-info" />
              </OverlayTrigger>
            </>
          )}
        </>
      ),
      'survey.name': lang === 'PL' ? survey.name : survey.nameEn,
      frequency_enum: t(SurveyFrequencyObj[survey.frequency]),
      contractor_type: t(ContractorTypeObj[survey.contractorType]),
      'survey.validityPeriodDays': survey.validityPeriodDays,
      survey_remind_yes_no: survey.remind ? t(tk.common.yes) : t(tk.common.no),
      'survey.startDate': momencik(survey.startDate),
      'survey.endDate': momencik(survey.endDate),
      'company.shortname':
        survey.surveyCompanies?.map(sc => sc.company?.shortname).join(', ') ||
        '',
      'employee.name':
        survey.surveyEmployees?.map(se => `${se.employee?.name}`).join(', ') ||
        '',
      survey_active_yes_no: survey.active ? t(tk.common.yes) : t(tk.common.no),
      preview:
        survey.questions?.length && survey.questions.length > 0 ? (
          <SurveyPreviewLink surveyId={survey.id} />
        ) : (
          ''
        ),
    },
    isInactive: !survey.active,
  };
};

export const SurveySearch: React.FC<SurveySearchProps> = props => {
  const { t, tk } = useTranslation('administrationLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'survey.systemNumber' },
    { header: t(tk.common.name), property: 'survey.name' },
    {
      header: t(tk.survey.frequency),
      property: 'frequency_enum',
    },
    {
      header: t(tk.survey.contractorType),
      property: 'contractor_type',
    },
    {
      header: t(tk.survey.validityPeriodDays),
      property: 'survey.validityPeriodDays',
    },
    {
      header: t(tk.survey.remindShort),
      property: 'survey_remind_yes_no',
    },
    { header: t(tk.survey.startDate), property: 'survey.startDate' },
    { header: t(tk.survey.endDate), property: 'survey.endDate' },
    { header: t(tk.common.companies), property: 'company.shortname' },
    { header: t(tk.survey.responsiblePersons), property: 'employee.name' },
    {
      header: t(tk.common.activeShe),
      property: 'survey_active_yes_no',
    },
    {
      header: '',
      property: 'preview',
      noFilter: true,
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="survey.systemNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.survey.noSurveys)}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={true}
      withFilterInColumns={true}
    />
  );
};
